import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const H = 6.62607015e-34; // Planck's constant

export default function DeBroglieCalculator() {
  const [mass, setMass] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "de Broglie Wavelength Calculator", url: "https://calclab.app/calculator/de-broglie" }
    ], lang),
    (lang) => calculatorToolSchema(
      "de Broglie Wavelength Calculator",
      "Calculate matter wave wavelength using de Broglie equation. Free online quantum physics calculator.",
      "https://calclab.app/calculator/de-broglie",
      lang
    )
  ]);
  const [velocity, setVelocity] = useState("");
  const [momentum, setMomentum] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateFromVelocity = () => {
    const m = parseFloat(mass);
    const v = parseFloat(velocity);

    if (isNaN(m) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    const p = m * v;
    const lambda = H / p;
    setResult(lambda);
    toast.success("Calculation complete!");
  };

  const calculateFromMomentum = () => {
    const p = parseFloat(momentum);

    if (isNaN(p) || p === 0) {
      toast.error("Please enter valid momentum (must be non-zero)");
      return;
    }

    const lambda = H / p;
    setResult(lambda);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setVelocity("");
    setMomentum("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="de Broglie Wavelength Calculator" calculatorPath="/calculator/de-broglie">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">de Broglie Wavelength Calculator</h1>
          <p className="text-muted-foreground">
            Calculate matter wave wavelength
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: λ = h/p = h/(mv)</CardTitle>
            <CardDescription>
              Where h = 6.626×10⁻³⁴ J·s (Planck's constant)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="velocity" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="velocity">From Velocity</TabsTrigger>
                <TabsTrigger value="momentum">From Momentum</TabsTrigger>
              </TabsList>

              <TabsContent value="velocity" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mass">Mass (kg)</Label>
                    <Input
                      id="mass"
                      type="number"
                      step="any"
                      placeholder="e.g., 9.11e-31 (electron)"
                      value={mass}
                      onChange={(e) => setMass(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Electron: 9.11×10⁻³¹ kg</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="velocity">Velocity (m/s)</Label>
                    <Input
                      id="velocity"
                      type="number"
                      step="any"
                      placeholder="Enter velocity"
                      value={velocity}
                      onChange={(e) => setVelocity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromVelocity} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="momentum" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="momentum">Momentum (kg·m/s)</Label>
                    <Input
                      id="momentum"
                      type="number"
                      step="any"
                      placeholder="Enter momentum"
                      value={momentum}
                      onChange={(e) => setMomentum(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromMomentum} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">de Broglie Wavelength</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toExponential(4)} m
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Matter wave wavelength
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["de-broglie"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• All matter has wave-like properties</p>
                <p>• Wavelength inversely proportional to momentum</p>
                <p>• Significant for small particles (electrons, atoms)</p>
                <p>• Negligible for macroscopic objects</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Electron at 10⁶ m/s → λ ≈ 7.3×10⁻¹⁰ m
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
